import React from 'react';

import {
  get,
  isArray,
  isEmpty,
  isNil,
} from 'lodash';

import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  ImageField,
  RecordContextProvider,
  TextField,
  usePermissions,
  useRecordContext,
  useRedirect,
} from 'react-admin';

import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import { Alpha3Code } from 'i18n-iso-countries';

import { Route, Routes } from 'react-router-dom';

import { ArrowForwardIosOutlined, BusinessCenter } from '@mui/icons-material';

import useEntityData from '../../../../hooks/useEntityData';

import actions, { Actions } from '../../../../constants/actions';
import { boldDataGridStyle } from '../../../../constants/style/datagridStyles';
import { ViewJSONDialog } from '../../../../customFields/ViewJSON';

import NoResults from '../../../layout/NoResults';
import PurchaseButton from '../../../layout/button/PurchaseButton';
import EntityData from './EntityData';
import Pagination from '../../../layout/Pagination';
import { EntityTypes } from '../../../../constants/entityTypes';
import { RelatedEntityRelations } from '../../../../constants/relatedEntityRelations';
import { SanctionTypes, sanctionTypesLabels } from '../../../../constants/sanctionTypes';
import ColoredScoreField from '../../../../customFields/ColoredScoreField';
import BusinessSanctionDetails from './sanction/details';
import hasAccess from '../../../../utilities/hasAccess';
import PermissionMissing from '../../../utils/PermissionMissing';

type SanctionListCompany = {
  id: string;
  createdAt: string;
  potentialMatches: {
    _id?: string;
    matchProbability?: number;
    providerId?: string;
    fullName?: string;
    otherNames: {
      fullName?: string;
    }[];
    address?: {
      streetAddress?: string;
      additionalAddress?: string;
      postcode?: string;
      city?: string;
      state?: string;
      country?: Alpha3Code;
      description?: string;
    };
    otherAddresses: {
      streetAddress?: string;
      additionalAddress?: string;
      postcode?: string;
      city?: string;
      state?: string;
      country?: Alpha3Code;
      description?: string;
    }[];
    registrationCountry?: Alpha3Code;
    registrationNumber?: string;
    sanctions: {
      providerId?: string;
      country?: Alpha3Code;
      issuingBody?: string;
      regime?: string;
      sanctionType: SanctionTypes;
      measures?: string[];
      fromDate?: string;
      toDate?: string;
    }[];
    relatedEntities: {
      entityId?: string;
      entityType?: EntityTypes;
      providerId?: string;
      entityName: string;
      relationshipType?: RelatedEntityRelations;
      relationshipDetails?: string;
      fromDate?: string;
      toDate?: string;
    }[];
    profileImages: string[];
    sourceDetails: {
      providerId?: string;
      title?: string;
      description?: string;
      link?: string;
      documentLink?: string;
      dateAdded?: string;
      datePublished?: string;
      isCopyrighted?: boolean;
      publicationSource?: string;
    }[];
    createdAt?: string;
    updatedAt?: string;
  }[];
  isTruncated?: boolean;
}

const RedirectToEntityButton = ({ label }: { label?: string }) => {
  const redirect = useRedirect();
  const record = useRecordContext<NonNullable<SanctionListCompany['potentialMatches'][number]>>();
  if (isEmpty(record)) return null;
  return (
    <IconButton onClick={() => redirect(`${record._id}`)} aria-label={label}>
      <ArrowForwardIosOutlined />
    </IconButton>
  );
};

const SanctionListBody = ({
  data,
  handlePurchase,
  isLoading,
}: {
  data: SanctionListCompany | undefined;
  handlePurchase: () => void;
  isLoading: boolean;
}) => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <RecordContextProvider value={data}>
      <Card variant="outlined">
        <CardHeader
          title="Sanction list"
          subheader={(
            hasAccess(permissions, actions.BUSINESS_GET_SANCTION_LIST_COMPANY) && (
              <Box display="flex" alignItems="baseline">
                <Typography>Latest update:</Typography>
                <DateField sx={{ marginLeft: 1, fontWeight: 'bold' }} source="createdAt" emptyText="-" showTime />
              </Box>
            )
          )}
          action={(
            <Box display="flex" gap={4}>
              { hasAccess(permissions, actions.BUSINESS_GET_SANCTION_LIST_COMPANY) && (<ViewJSONDialog title="Inspect raw data" />)}
              <PurchaseButton
                loading={isLoading}
                label="Get sanction list"
                onClick={handlePurchase}
                empty={!isArray(data?.potentialMatches)}
                action={actions.BUSINESS_CREATE_SANCTION_LIST_COMPANY}
              />
            </Box>
          )}
        />
        <Divider />

        {data?.isTruncated && (
          <Grid container justifyContent="center" marginTop={2}>
            <Alert severity="warning">This screening report contains truncated results because there were too many potential matches.</Alert>
          </Grid>
        )}
        { hasAccess(permissions, actions.BUSINESS_GET_SANCTION_LIST_COMPANY) ? (
          <ArrayField source="potentialMatches" perPage={15}>
            <Datagrid empty={<NoResults variant="h6" />} sx={{ ...boldDataGridStyle, width: '100%' }} bulkActionButtons={false}>
              <FunctionField
                label="Names"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  const { fullName } = record;
                  if (!fullName) return '-';
                  const names = [{ fullName }, ...record.otherNames];
                  return names
                    .map((name) => name.fullName ?? '')
                    .slice(0, 5).join('\n');
                }}
              />
              <ColoredScoreField source="matchProbability" label="Match %" />
              <TextField source="registrationNumber" emptyText="-" />
              <TextField source="registrationCountry" emptyText="-" />
              <FunctionField
                label="Issuing body"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const issuingBodies = record.sanctions.map((sanction) => sanction.issuingBody)
                    .filter((value) => !isNil(value));
                  if (isEmpty(issuingBodies)) return '-';
                  return issuingBodies.join('\n');
                }}
              />
              <FunctionField
                label="Regime"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const regimes = record.sanctions.map((sanction) => sanction.regime)
                    .filter((value) => !isNil(value));
                  if (isEmpty(regimes)) return '-';
                  return regimes.join('\n');
                }}
              />
              <FunctionField
                label="Relations"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  if (isEmpty(record.relatedEntities)) return '-';
                  const relatedEntities = record.relatedEntities
                    .map((relatedEntity) => relatedEntity.entityName)
                    .filter((text) => !isNil(text));
                  if (isEmpty(relatedEntities)) return '-';
                  return relatedEntities.join('\n');
                }}
              />
              <FunctionField
                label="Sanction type"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const sanctionTypes = record.sanctions
                    .map((sanction) => get(sanctionTypesLabels, sanction.sanctionType, null))
                    .filter((value) => !isNil(value));
                  if (isEmpty(sanctionTypes)) return '-';
                  return sanctionTypes.join('\n');
                }}
              />
              <FunctionField
                label="Image"
                render={(record: SanctionListCompany['potentialMatches'][number]) => {
                  if (isEmpty(record.profileImages)) return <BusinessCenter />;
                  return <ImageField source="profileImages[0]" />;
                }}
              />
              <RedirectToEntityButton />
            </Datagrid>
            <Pagination disableEmptyText />
          </ArrayField>
        ) : (
          <Box margin={5} display="flex" alignItems="center" width="100%" flexDirection="column">
            <PermissionMissing />
          </Box>
        )}
      </Card>
    </RecordContextProvider>
  );
};

const SanctionList = () => {
  const {
    data,
    handlePurchase,
    isLoading,
  } = useEntityData<SanctionListCompany>({
    resource: 'businesses',
    source: 'sanction-list',
    getAction: actions.BUSINESS_GET_SANCTION_LIST_COMPANY,
  });

  return (
    <Routes>
      <Route
        index
        element={(
          <Slide in direction="right" appear={false}>
            <Grid container spacing={6}>
              <Grid size={12}>
                <EntityData />
              </Grid>
              <Grid size={12}>
                <SanctionListBody
                  data={data}
                  handlePurchase={handlePurchase}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
          </Slide>
        )}
      />
      <Route path=":sanctionId" element={<BusinessSanctionDetails />} />
    </Routes>
  );
};

export default SanctionList;
